
const POI_MARKER_BASE = '/assets/images/poi';

export type PoiCategory = {
  translationKey: string;

  icon: string;
  super_categories: readonly string[];
  sub_categories: readonly string[];
};

/**
 * POI Categories Map (This is for the ammenties filter panel):
 * - translationKey: Display key for category
 * - icon - map marker for category
 * - In backend there are super categories and sub categories within each super category. Each category/ammentity on UI maps to 
 *  particular super categories or sub categories.
 * 
 **/
  
export const POI_CATEGORY_MAP = {
  essentials: {
    translationKey: 'search:poi.category_labels.essentials',
    icon: `${POI_MARKER_BASE}/essentials.png`,
    super_categories: ['Essentials'],
    sub_categories: ['Grocery Stores', 'Banks and ATMs', 'Post Offices', 'Gas Stations', 'Pharmacies'],
  },
  lifestyle: {
    translationKey: 'search:poi.category_labels.lifestyle',
    icon: `${POI_MARKER_BASE}/food.png`,
    super_categories: ['Lifestyle'],
    sub_categories: ['Coffee Shops', 'Dog Parks', 'Gyms & Fitness Centers'],
  },
  commute: {
    translationKey: 'search:poi.category_labels.commute',
    icon: `${POI_MARKER_BASE}/commute.png`,
    super_categories: ['Commute'],
    sub_categories: ['Commuter Rail Stops', 'Highway Access Points', 'Public Transit Stations'],
  },
  education: {
    translationKey: 'search:poi.category_labels.education',
    icon: `${POI_MARKER_BASE}/education.png`,
    super_categories: ['Education'],
    sub_categories: ['Daycare and Preschools', 'Elementary & High Schools'],
  },
  recreation: {
    translationKey: 'search:poi.category_labels.recreation',
    icon: `${POI_MARKER_BASE}/recreation.png`,
    super_categories: ['Recreation'],
    sub_categories: ['Parks and Playgrounds', 'Hiking and Biking Trails'],
  },
  entertainment: {
    translationKey: 'search:poi.category_labels.entertainment',
    icon: `${POI_MARKER_BASE}/entertainment.png`,
    super_categories: ['Entertainment'],
    sub_categories: ['Movie Theaters', 'Restaurants and Bars'],
  },
  community: {
    translationKey: 'search:poi.category_labels.community',
    icon: `${POI_MARKER_BASE}/community.png`,
    super_categories: ['Community'],
    sub_categories: ['Community Centers', 'Libraries'],
  },
  infrastructure: {
    translationKey: 'search:poi.category_labels.infrastructure',
    icon: `${POI_MARKER_BASE}/ev-parking.png`,
    super_categories: ['Infrastructure'],
    sub_categories: ['EV Charging Stations'],
  },
  health: {
    translationKey: 'search:poi.category_labels.health',
    icon: `${POI_MARKER_BASE}/health.png`,
    super_categories: ['Health'],
    sub_categories: ['Hospitals & Urgent Care'],
  },
  safety: {
    translationKey: 'search:poi.category_labels.safety',
    icon: `${POI_MARKER_BASE}/essentials.png`,
    super_categories: ['Safety'],
    sub_categories: ['Police and Fire Stations'],
  },
  retail: {
    translationKey: 'search:poi.category_labels.retail',
    icon: `${POI_MARKER_BASE}/retail.png`,
    super_categories: ['Retail'],
    sub_categories: ['Shopping Centers & Malls'],
  },
  pets: {
    translationKey: 'search:poi.category_labels.pets',
    icon: `${POI_MARKER_BASE}/pets.png`,
    super_categories: ['Pets'],
    sub_categories: ['Veterinary Clinics'],
  },
} satisfies Record<string, PoiCategory>;

export type PoiCategoryId = keyof typeof POI_CATEGORY_MAP;

const DEFAULT_MAP_MARKER_PNG = POI_CATEGORY_MAP.essentials.icon;


export function getIconForPoiCategory(superCategory: string): string {
  const needle = superCategory.trim().toLowerCase();
  if (!needle) return DEFAULT_MAP_MARKER_PNG;
  for (const id of Object.keys(POI_CATEGORY_MAP) as PoiCategoryId[]) {
    const def = POI_CATEGORY_MAP[id];
    if (def.super_categories.some(s => s.trim().toLowerCase() === needle)) {
      return def.icon;
    }
  }
  return DEFAULT_MAP_MARKER_PNG;
}

export function superCategoriesFromSelectedIds(selected: ReadonlySet<PoiCategoryId>): string[] {
  const supers = new Set<string>();
  for (const id of selected) {
    const def = POI_CATEGORY_MAP[id];
    for (const s of def.super_categories) {
      supers.add(s);
    }
  }
  return [...supers].sort();
}
